import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Upload, FileText, Download, Trash2, Loader2, Eye } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { getEmployeeFiles, uploadEmployeeFile, downloadEmployeeFile, deleteEmployeeFile } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { EventAttachmentPreview } from "./EventAttachmentPreview";

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} Mo` : `${Math.round(b / 1024)} Ko`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "");
const periodLabel = (p) => p ? new Date(p + "-01T00:00:00").toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) : null;

export const FileSection = ({ employeeId, category, emptyLabel, withPeriod }) => {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || (category !== "payslip" && !!user?.can_manage_events);
  const [preview, setPreview] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await getEmployeeFiles(employeeId, { category });
      setFiles(res.data);
    } catch {
      toast.error("Erreur de chargement des fichiers");
    } finally {
      setLoading(false);
    }
  }, [employeeId, category]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      if (withPeriod && period) fd.append("period", period);
      await uploadEmployeeFile(employeeId, fd);
      toast.success("Fichier ajouté");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDownload = async (f) => {
    try {
      const res = await downloadEmployeeFile(f.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = f.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur de téléchargement");
    }
  };

  const handleDelete = async (f) => {
    try {
      await deleteEmployeeFile(f.id);
      toast.success("Fichier supprimé");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div data-testid={`file-section-${category}`}>
      {canManage && (
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {withPeriod && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Mois :</span>
            <Input type="month" className="w-40 h-9" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid={`period-input-${category}`} />
          </div>
        )}
        <input ref={inputRef} type="file" className="hidden" onChange={handleUpload} accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" data-testid={`employee-file-input-${category}`} />
        <Button size="sm" className="bg-[#26788A] hover:bg-[#1D5E6C] text-white ml-auto" disabled={uploading} onClick={() => inputRef.current?.click()} data-testid={`upload-btn-${category}`}>
          {uploading ? <Loader2 size={14} className="animate-spin mr-1" /> : <Upload size={14} className="mr-1" />} Ajouter un fichier
        </Button>
      </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 size={22} className="animate-spin text-slate-300" /></div>
      ) : files.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-8 bg-slate-50 rounded-lg border border-dashed border-slate-200">{emptyLabel}</p>
      ) : (
        <div className="space-y-1.5">
          {files.map((f) => (
            <div key={f.id} className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2" data-testid={`file-row-${f.id}`}>
              <FileText size={18} className="text-[#26788A] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{f.filename}</p>
                <p className="text-xs text-slate-400 capitalize">
                  {f.period ? `${periodLabel(f.period)} · ` : ""}{fmtSize(f.size)} · ajouté le {fmtDate(f.uploaded_at)}
                </p>
              </div>
              <Button variant="ghost" size="sm" className="h-8 text-[#26788A]" onClick={() => handleDownload(f)} data-testid={`download-file-${f.id}`}>
                <Download size={15} />
              </Button>
              {category === "payslip" && <Button variant="ghost" size="icon" aria-label="Consulter le bulletin" data-testid={`preview-file-${f.id}`} onClick={async () => { try { const result = await downloadEmployeeFile(f.id); setPreview({ ...f, file: result.data }); } catch { toast.error("Bulletin inaccessible"); } }}><Eye size={16} /></Button>}
              {canManage && (
              <Button variant="ghost" size="sm" className="h-8 text-slate-400 hover:text-red-500" onClick={() => handleDelete(f)} data-testid={`delete-file-${f.id}`}>
                <Trash2 size={15} />
              </Button>
              )}
            </div>
          ))}
        </div>
      )}
      {preview && <EventAttachmentPreview attachment={preview} onClose={() => setPreview(null)} />}
    </div>
  );
};
